import React from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import { FileText, LayoutDashboard, LogOut, Search, ShoppingCart, User } from "lucide-react";

export const Brand = ({ compact = false }) => (
  <Link to="/consulta" className="flex items-center gap-2.5 shrink-0" data-testid="brand-link">
    <img
      src="/brand/logo-skymaster-horizontal.png"
      alt="SKYMASTER Condutores Elétricos"
      className={compact ? "h-6 w-auto sm:h-7" : "h-7 w-auto sm:h-8"}
    />
  </Link>
);

export const AppHeader = () => {
  const { user, logout, isAdmin } = useAuth();
  const { quantidadeItens } = useCart();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const navItens = [
    { to: "/consulta", label: "Consulta", icon: Search },
    { to: "/orcamento", label: "Orçamento", icon: ShoppingCart, badge: quantidadeItens },
    { to: "/orcamentos", label: "Histórico", icon: FileText },
  ];

  return (
    <header className="sticky top-0 z-40 border-b border-slate-200 bg-white">
      <div className="mx-auto flex h-14 max-w-7xl items-center gap-3 px-3 sm:px-6">
        <Brand />
        <nav className="ml-2 hidden items-center gap-1 md:flex">
          {navItens.map((n) => {
            const ativo = pathname.startsWith(n.to);
            return (
              <Link key={n.to} to={n.to} data-testid={`nav-${n.label.toLowerCase()}`}>
                <span
                  className={`inline-flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-medium transition-colors ${
                    ativo
                      ? "bg-primary/10 text-primary"
                      : "text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                  }`}
                >
                  <n.icon className="h-4 w-4" />
                  {n.label}
                  {n.badge > 0 && (
                    <Badge className="ml-0.5 h-5 min-w-5 justify-center bg-primary px-1.5 text-[11px] text-white">
                      {n.badge}
                    </Badge>
                  )}
                </span>
              </Link>
            );
          })}
        </nav>

        <div className="ml-auto flex items-center gap-2">
          {isAdmin && (
            <Button
              variant="outline"
              size="sm"
              className="hidden sm:inline-flex"
              onClick={() => navigate("/admin")}
              data-testid="header-admin-button"
            >
              <LayoutDashboard className="mr-1.5 h-4 w-4" />
              Administração
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="gap-2" data-testid="header-user-menu">
                <span className="grid h-7 w-7 place-items-center rounded-full bg-slate-900 text-[11px] font-semibold text-white">
                  {(user?.nome || "?").slice(0, 2).toUpperCase()}
                </span>
                <span className="hidden max-w-[140px] truncate text-sm font-medium sm:block">
                  {user?.nome}
                </span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60 bg-white">
              <DropdownMenuLabel>
                <div className="text-sm font-semibold text-slate-900">{user?.nome}</div>
                <div className="truncate text-xs font-normal text-slate-500">{user?.email}</div>
                <Badge variant="secondary" className="mt-1.5 text-[10px] uppercase">
                  {user?.role === "admin" ? "Administrador" : "Representante"}
                </Badge>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/perfil")} data-testid="menu-perfil">
                <User className="mr-2 h-4 w-4" /> Minha conta
              </DropdownMenuItem>
              {isAdmin && (
                <DropdownMenuItem onClick={() => navigate("/admin")} data-testid="menu-admin">
                  <LayoutDashboard className="mr-2 h-4 w-4" /> Administração
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-destructive" data-testid="menu-sair">
                <LogOut className="mr-2 h-4 w-4" /> Sair
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* nav mobile */}
      <nav className="flex items-center gap-1 overflow-x-auto border-t border-slate-200 px-3 py-1.5 md:hidden">
        {navItens.map((n) => {
          const ativo = pathname.startsWith(n.to);
          return (
            <Link key={n.to} to={n.to} className="shrink-0" data-testid={`navm-${n.label.toLowerCase()}`}>
              <span
                className={`inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium transition-colors ${
                  ativo ? "bg-primary/10 text-primary" : "text-slate-600"
                }`}
              >
                <n.icon className="h-4 w-4" />
                {n.label}
                {n.badge > 0 && (
                  <Badge className="h-4 min-w-4 justify-center bg-primary px-1 text-[10px] text-white">
                    {n.badge}
                  </Badge>
                )}
              </span>
            </Link>
          );
        })}
        {isAdmin && (
          <Link to="/admin" className="shrink-0" data-testid="navm-admin">
            <span className="inline-flex h-9 items-center gap-1.5 rounded-lg px-3 text-[13px] font-medium text-slate-600">
              <LayoutDashboard className="h-4 w-4" /> Admin
            </span>
          </Link>
        )}
      </nav>
    </header>
  );
};
